import { useState, useCallback, useMemo } from "react";
import type {
  GeneratedCodeFilters,
  UseGeneratedCodeFiltersReturn,
} from "@/types/generatedCode";

/**
 * Hook personnalisé pour gérer les filtres des codes générés
 * Centralise la logique de filtrage et optimise les performances
 */
export const useGeneratedCodeFilters = (): UseGeneratedCodeFiltersReturn => {
  // États des filtres
  const [searchQuery, setSearchQuery] = useState("");
  const [rubriqueFilter, setRubriqueFilter] = useState<string>("all");
  const [tagFilter, setTagFilter] = useState<string>("all");
  const [showTemplatesOnly, setShowTemplatesOnly] = useState(false);

  /**
   * Construit l'objet de filtres pour l'API
   * Memoized pour éviter les recalculs inutiles
   */
  const filters = useMemo<GeneratedCodeFilters>(() => {
    const result: GeneratedCodeFilters = {};

    if (searchQuery.trim()) {
      result.search = searchQuery.trim();
    }

    if (rubriqueFilter !== "all") {
      result.rubriques = [rubriqueFilter];
    }

    if (tagFilter !== "all") {
      result.tags = [tagFilter];
    }

    if (showTemplatesOnly) {
      result.isTemplate = true;
    }

    return result;
  }, [searchQuery, rubriqueFilter, tagFilter, showTemplatesOnly]);

  /**
   * Réinitialise tous les filtres
   */
  const resetFilters = useCallback(() => {
    setSearchQuery("");
    setRubriqueFilter("all");
    setTagFilter("all");
    setShowTemplatesOnly(false);
  }, []);

  /**
   * Vérifie si des filtres sont actifs
   */
  const hasActiveFilters = useMemo(() => {
    return (
      searchQuery.trim() !== "" ||
      rubriqueFilter !== "all" ||
      tagFilter !== "all" ||
      showTemplatesOnly
    );
  }, [searchQuery, rubriqueFilter, tagFilter, showTemplatesOnly]);

  return {
    // États
    searchQuery,
    rubriqueFilter,
    tagFilter,
    showTemplatesOnly,

    // Setters
    setSearchQuery,
    setRubriqueFilter,
    setTagFilter,
    setShowTemplatesOnly,

    // Valeurs calculées
    filters,
    hasActiveFilters,

    // Actions
    resetFilters,
  };
};
